// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {MeasureUtil} from 'common/util';
import {ApplyFilterEvent} from 'common/components/filter-bar/types';
import {SelectChangeEvent} from 'common/components/filter-bar-table/types';
import mpFonts from 'common/stylesheets/mixins/fonts.json';
import {IronContextRegistry} from 'common/report/context-registry';
import {SectionItem} from 'common/components/items-menu/types';
import {getConfigDefaultValue, getItemValue} from 'common/components/filter-bar/utils';

import {template} from './template';
import css from './index.inline.styl';
import {
  ActionOption,
  FilterComplexConfig,
  FilterMenuChangeEvent,
  FilterSelectAllEvent,
  FilterSelectConfig,
  FilterSimpleConfig,
  FilterType,
  GetFilterSortedMenuSections,
  UpdateInputEvent,
} from './types';

interface FilterBarState {
  isSearchInputExpanded: boolean;
  searchText: string;
  openedFilters: {[name: string]: boolean};
  getFilterSortedMenuSections: GetFilterSortedMenuSections;
}

export interface FilterBarJsxProps {
  attrs?: {
    actions?: ActionOption[];
    'always-show-count'?: boolean;
    elref?: string;
    filters?: FilterSelectConfig[];
    'action-button-theme'?: string;
    'hide-filter-by-text'?: boolean;
    'hide-reset-filters'?: boolean;
    'hide-search'?: boolean;
    'result-count'?: string;
    'selected-count'?: number;
    'search-placeholder'?: string;
    'search-first'?: boolean;
    'search-text'?: string;
    'sticky-titles'?: boolean;
  };
  params?: {
    filters?: FilterSelectConfig[];
    actions?: ActionOption[];
  };
  props?: {
    getFilterSortedMenuSections?: GetFilterSortedMenuSections;
  };
  on?: {
    clickAction?: (ev: SelectChangeEvent) => void;
    clearSelected?: () => void;
    changeFilter?: (ev: SelectChangeEvent) => void;
    applyFilter?: (ev: ApplyFilterEvent) => void;
    filterMenuChange?: (ev: FilterMenuChangeEvent) => void;
    filterSelectAll?: (ev: FilterSelectAllEvent) => void;
    updateInput?: (ev: UpdateInputEvent) => void;
  };
}

export class FilterBar extends Component<
  FilterBarState,
  FilterBarJsxProps[`attrs`],
  unknown,
  unknown,
  IronContextRegistry,
  FilterBarJsxProps[`params`]
> {
  static get attrsSchema(): AttrsSchema<FilterBarJsxProps[`attrs`]> {
    return {
      ...super.attrsSchema,
      'always-show-count': `boolean`,
      'action-button-theme': {type: `string`, default: `tertiary`},
      actions: {type: `json`, description: `Array of ActionOption to show when items are selected`, default: []},
      elref: `string`,
      'hide-filter-by-text': `boolean`,
      'hide-reset-filters': {type: `boolean`, description: `Hide 'reset filters' button`},
      'hide-search': {type: `boolean`, description: `Hide the search input`},
      filters: {type: `json`, description: `Array of FilterSelectConfig to display as filter dropdowns`, default: []},
      'result-count': {type: `string`, description: `Number of results`},
      'selected-count': {type: `number`, description: `Number of selected items`},
      'search-placeholder': {type: `string`, description: `Placeholder text for empty search`, default: `Search`},
      'search-text': {type: `string`, description: `Search input text`},
      'search-first': {type: `boolean`, description: `whether to display the filters first or the search first`},
      'sticky-titles': `boolean`,
    };
  }
  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        isSearchInputExpanded: false,
        searchText: ``,
        openedFilters: {},
        getFilterSortedMenuSections: null,
      },
      contexts: [`i18n` as const],
      params: {
        actions: Array,
        filters: Array,
      },
      helpers: {
        FilterType,
        isFilterOpen: (filterConfig) => this.isFilterOpen(filterConfig),
        getFilterSelectedLabel: (filterSelectConfig: FilterSelectConfig) => {
          const selectedItems = this.selectedItemsFor(filterSelectConfig);
          let label = null;

          if (filterSelectConfig.label) {
            label = filterSelectConfig.label;
          }
          if (label === null && selectedItems.length > 0) {
            label = filterSelectConfig.multiple
              ? this.t(`components.filter-bar.x-selected`, {count: selectedItems.length})
              : selectedItems[0].label || selectedItems[0].value;
          }
          if (label === null) {
            label = this.t(`components.filter-bar.select-item`, {itemType: filterSelectConfig.name});
          }

          return label;
        },
        handleClickAction: (eventName, item = null) => {
          this.dispatchEvent(new CustomEvent(`clickAction`, {detail: {name: eventName, item}}));
        },

        handleClearSelected: () => this.dispatchEvent(new CustomEvent(`clearSelected`)),

        handleFilterChanged: (ev) => {
          this.dispatchEvent(new CustomEvent(`changeFilter`, {detail: ev.detail}));
        },

        handleFilterReset: () =>
          this.getFilters().forEach((filter) => {
            switch (filter.filterType) {
              case FilterType.Complex:
                // Not implemented
                break;
              case FilterType.Simple:
              default: {
                if (getConfigDefaultValue(filter)) {
                  this.config.helpers.handleFilterChanged({
                    detail: {
                      value: filter.defaultFilterLabel,
                      item: {
                        value: filter.defaultFilterValue,
                        label: filter.defaultFilterLabel,
                      } satisfies SectionItem,
                      name: filter.name,
                    },
                  });
                }
                break;
              }
            }
          }),
        handleFilterApplyClicked: (ev) => {
          this.dispatchEvent(new CustomEvent(`applyFilter`, {detail: ev.detail}));
        },

        handleMenuChange: (ev, name) => {
          const {isOpen} = ev.detail;

          this.dispatchEvent(
            new CustomEvent(`filterMenuChange`, {
              detail: {
                name,
                isOpen,
              },
            }),
          );
        },

        handleAllSelected: ({detail}, name) => {
          this.dispatchEvent(
            new CustomEvent(`filterSelectAll`, {
              detail: {
                ...detail,
                name,
              },
            }),
          );
        },

        isFilterSectionHidden: () => {
          return (
            this.getFilters().every((filter) => !this.selectedItemsFor(filter).length) ||
            (this.getFilters().length === 1 && this.getFilters()[0].filterType === FilterType.Complex)
          );
        },

        isFilterSelected: () =>
          this.getFilters().some((filter) => {
            switch (filter.filterType) {
              case FilterType.Complex:
              case FilterType.Simple:
              default:
                return this.itemsFor(filter).some(
                  (item) => this.getItemValue(item) !== getConfigDefaultValue(filter) && item.isSelected,
                );
            }
          }),

        handleUpdateInput: (value) => {
          this.update({searchText: value, isSearchInputExpanded: true});
          this.dispatchEvent(
            new CustomEvent(`updateInput`, {
              detail: {
                value,
              },
            }),
          );
        },

        handleInputExpanded: (expand: boolean) => this.update({isSearchInputExpanded: expand}),

        getTextWidth: () => {
          const font = `400 16px ${mpFonts.fontFamilyPrimary}`;
          const paddingLeft = 50; // search icon and other padding
          const paddingRight = 30; // x icon and padding
          const maxWidth = 320;
          if (this.state.isSearchInputExpanded) {
            return maxWidth;
          } else if (!this.state.searchText) {
            return MeasureUtil.measureTextWidth(this.attr(`search-placeholder`), font) + paddingLeft;
          } else {
            return Math.min(
              maxWidth,
              MeasureUtil.measureTextWidth(this.state.searchText, font) + paddingLeft + paddingRight,
            );
          }
        },
      },
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  getFilters() {
    return this.params.filters || this.attr(`filters`);
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    // support updating searchText via attr as well
    if (attr === `search-text`) {
      this.update({searchText: this.attr(attr)});
    }
  }

  isFilterOpen(filterConfig) {
    return this.state.openedFilters[filterConfig.name];
  }

  closeFilter(filterConfig) {
    this.update({
      openedFilters: {
        ...this.state.openedFilters,
        [filterConfig.name]: false,
      },
    });
  }

  selectedItemsFor(filterSelectConfig: FilterSelectConfig) {
    return this.itemsFor(filterSelectConfig).filter((item) => item.isSelected);
  }

  itemsFor(filterSelectConfig: FilterSimpleConfig | FilterComplexConfig) {
    return (filterSelectConfig.sections || []).map(({items}) => items).flat();
  }

  set getFilterSortedMenuSections(getFilterSortedMenuSections: GetFilterSortedMenuSections) {
    this.update({getFilterSortedMenuSections});
  }

  getItemValue(item: SectionItem) {
    return this.getContext(`i18n`).print(getItemValue(item));
  }
}

export const FilterBarEl = defineAndMakeJsxFactory<FilterBarJsxProps>(`mp-filter-bar`, FilterBar);
export const FilterBarReact = panel2react<FilterBarJsxProps>(`mp-filter-bar`, FilterBar);
